import React, { useState, memo } from 'react';
import { 
  Button, 
  Input, 
  Select, 
  SelectItem, 
  Chip, 
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@heroui/react';
import { Plus, Hash, Trash, Globe, Users } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { Parameter } from '../types';

export const ParameterManager: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [newParamName, setNewParamName] = useState('');
  const [newParamType, setNewParamType] = useState<'global' | 'individual'>('global');

  // Auto-detect parameters from content (currently unused)
  // const autoDetectParameters = () => {
    const content = state.emailSubject + ' ' + state.emailContent;
    const parameterRegex = /\{(gl|ind)_([a-zA-Z0-9_]+)\}/g;
    const matches = [...content.matchAll(parameterRegex)];
    const existingPlaceholders = new Set(state.parameters.map(p => p.placeholder));
    
    const newParameters: Parameter[] = [];
    
    matches.forEach(match => {
      const [placeholder, prefix, sanitizedName] = match;
      
      if (!existingPlaceholders.has(placeholder)) {
        const type = prefix === 'gl' ? 'global' : 'individual';
        const name = sanitizedName.replace(/_/g, ' ');
        
        const newParameter: Parameter = {
          id: `autodetected_${Date.now()}_${newParameters.length}`,
          name: name,
          placeholder: placeholder,
          type: type
        };
        
        newParameters.push(newParameter);
        existingPlaceholders.add(placeholder);
      }
    });
    
    if (newParameters.length > 0) {
      const updatedParameters = [...state.parameters, ...newParameters];
      dispatch({ type: 'SET_PARAMETERS', payload: updatedParameters });
      
      // Initialize parameter values
      const newValues = { ...state.parameterValues };
      newParameters.forEach(param => {
        if (param.type === 'global') {
          newValues[param.id] = '';
        } else {
          const individualValues: { [contactId: string]: string } = {};
          state.contacts.forEach(contact => {
            individualValues[contact.id] = '';
          });
          newValues[param.id] = individualValues;
        }
      });
      
      dispatch({ type: 'SET_PARAMETER_VALUES', payload: newValues });
      
      return newParameters.length;
    }
    
    // return 0;
  // };

  const createParameter = () => {
    if (!newParamName.trim()) return;
    
    // Create sanitized placeholder
    const sanitizedName = newParamName.trim().toLowerCase().replace(/\s+/g, '_');
    const prefix = newParamType === 'global' ? 'gl' : 'ind';
    const placeholder = `{${prefix}_${sanitizedName}}`;
    
    // Check if placeholder already exists
    const exists = state.parameters.some(p => p.placeholder === placeholder);
    if (exists) {
      alert('Een parameter met deze naam bestaat al!');
      return;
    }
    
    const newParameter: Parameter = {
      id: `custom_${Date.now()}`,
      name: newParamName.trim(),
      placeholder: placeholder,
      type: newParamType
    };
    
    const updatedParameters = [...state.parameters, newParameter];
    dispatch({ type: 'SET_PARAMETERS', payload: updatedParameters });
    
    // Initialize parameter value
    const newValues = { ...state.parameterValues };
    if (newParamType === 'global') {
      newValues[newParameter.id] = '';
    } else {
      const individualValues: { [contactId: string]: string } = {};
      state.contacts.forEach(contact => {
        individualValues[contact.id] = '';
      });
      newValues[newParameter.id] = individualValues;
    }
    
    dispatch({ type: 'SET_PARAMETER_VALUES', payload: newValues });
    
    // Reset form
    setNewParamName('');
    setNewParamType('global');
    onOpenChange();
  };

  const removeParameter = (parameterId: string) => {
    const updatedParameters = state.parameters.filter(p => p.id !== parameterId);
    dispatch({ type: 'SET_PARAMETERS', payload: updatedParameters });
    
    const updatedValues = { ...state.parameterValues };
    delete updatedValues[parameterId];
    dispatch({ type: 'SET_PARAMETER_VALUES', payload: updatedValues });
  };

  const updateParameterValue = (parameterId: string, value: string, contactId?: string) => {
    const updatedValues = { ...state.parameterValues };
    
    if (contactId) {
      // Individual parameter
      const individualValues = { ...updatedValues[parameterId] as { [key: string]: string } };
      individualValues[contactId] = value;
      updatedValues[parameterId] = individualValues;
    } else {
      // Global parameter
      updatedValues[parameterId] = value;
    }
    
    dispatch({ type: 'SET_PARAMETER_VALUES', payload: updatedValues });
  };

  const getParameterValue = (parameterId: string, contactId?: string): string => {
    const value = state.parameterValues[parameterId];
    
    if (contactId && typeof value === 'object') {
      return value[contactId] || '';
    }
    
    return typeof value === 'string' ? value : '';
  };

  return (
    <div id="dynamic-fields-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Parameters</h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            color="primary"
            variant="flat"
            startContent={<Plus size={14} />}
            onPress={onOpen}
          >
            Nieuwe Parameter
          </Button>
        </div>
      </div>

      {state.parameters.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Hash size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Geen parameters aangemaakt</p>
          <p className="text-xs mt-1">
            Gebruik parameters om dynamische content te personaliseren
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {state.parameters.map((parameter) => (
            <div key={parameter.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={parameter.type === 'global' ? 'primary' : 'secondary'}
                    startContent={parameter.type === 'global' ? <Globe size={12} /> : <Users size={12} />}
                  >
                    {parameter.type === 'global' ? 'Globaal' : 'Individueel'}
                  </Chip>
                  <h4 className="font-medium text-gray-900">{parameter.name}</h4>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded-sm">
                    {parameter.placeholder}
                  </code>
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => removeParameter(parameter.id)}
                >
                  <Trash size={14} />
                </Button>
              </div>

              {parameter.type === 'global' ? (
                <Input
                  value={getParameterValue(parameter.id)}
                  onChange={(e) => updateParameterValue(parameter.id, e.target.value)}
                  placeholder={`Waarde voor ${parameter.name}`}
                  size="sm"
                />
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Waarden per contact ({state.contacts.length} contacten):
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {state.contacts.map((contact) => (
                      <div key={contact.id} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-32 truncate">
                          {contact.name}
                        </span>
                        <Input
                          value={getParameterValue(parameter.id, contact.id)}
                          onChange={(e) => updateParameterValue(parameter.id, e.target.value, contact.id)}
                          placeholder={`${parameter.name} voor ${contact.firstName || 'contact'}`}
                          size="sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Parameter Modal */}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                <h3>Nieuwe Parameter</h3>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    label="Parameter naam"
                    value={newParamName}
                    onChange={(e) => setNewParamName(e.target.value)}
                    placeholder="bijv. 'bedrag' of 'deadline'"
                  />
                  
                  <Select
                    label="Type"
                    selectedKeys={[newParamType]}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string;
                      setNewParamType(selected as 'global' | 'individual');
                    }}
                  >
                    <SelectItem key="global">
                      Globaal - Zelfde waarde voor alle contacten
                    </SelectItem>
                    <SelectItem key="individual">
                      Individueel - Unieke waarde per contact
                    </SelectItem>
                  </Select>

                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <strong>Preview placeholder:</strong>{' '}
                    <code>{newParamName ? `{${newParamType === 'global' ? 'gl' : 'ind'}_${newParamName.toLowerCase().replace(/\s+/g, '_')}}` : '{type_naam}'}</code>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Annuleren
                </Button>
                <Button 
                  color="primary" 
                  onPress={createParameter}
                  isDisabled={!newParamName.trim()}
                >
                  Parameter Aanmaken
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
});
